import React, { useCallback } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import './style/index.scss';

export interface  UploadDropZoneProps {
  delayFile?: File[];
  file?: File;
  fileUploadSuccess: boolean;
  rejectReason: string;
  orgJobId?: number;
  showDropZoneUploading: boolean;
  prettyName: string;
  fileName: string;
  acceptedFileTypes: string[];
  itemToUpload: string;
  onDrop(acceptedFile: File[], orgJobId: number): void;
  getFileRejectReason(rejectededFile: FileRejection[]): void;
  resetFileRejectReason(): void;
  delayUpload(acceptedFile: File[]): void;
  removeFile(): void;
}

const UploadDropZone = (props: UploadDropZoneProps) => {
  const {
    orgJobId,
    delayFile,
    file,
    fileUploadSuccess,
    rejectReason,
    showDropZoneUploading,
    prettyName,
    fileName,
    acceptedFileTypes,
    onDrop: acceptFiles,
    getFileRejectReason,
    resetFileRejectReason,
    delayUpload,
    removeFile,
    itemToUpload
  } = props;

  const onDropAccepted = useCallback((acceptedFiles: File[]) => {
    resetFileRejectReason();
    if (itemToUpload === 'Job Description' && !orgJobId) {
      delayUpload(acceptedFiles);
    }
    acceptFiles(acceptedFiles, orgJobId as number);
  }, [acceptFiles, delayUpload, itemToUpload, orgJobId, resetFileRejectReason]);

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    getFileRejectReason(fileRejections);
  }, []);

  const getDragStyle = () => {
    if (isDragActive) {
      return 'upload-dropzone__area--active';
    }
    return '';
  };

  const acceptedFileTypesText = acceptedFileTypes.join(', ');

  const {
        getRootProps,
        getInputProps,
        isDragActive
    } = useDropzone({
          onDropAccepted,
          onDropRejected,
          accept: acceptedFileTypes,
          // max size is 50MB; maxSize param is in bytes
          maxSize: 50000000
        });

  // Ready for file upload
  let renderBlock = (
    <div className="upload-dropzone">
      <div className={'upload-dropzone__area ' + getDragStyle()} {...getRootProps()}>
      Upload a {itemToUpload}
      <input {...getInputProps({className: 'upload-dropzone__input'})} />
        <i className="icon-border icon-circle-thin">
          <i className="icon-up"/>
        </i>
        <div className="upload-dropzone__text">
          <span>Drag &amp; drop file here or </span>
          <span className="upload-dropzone__text-select">select file</span>
        </div>
      </div>
    </div>
  );

  let hideSuccessClass = 'hidden';
  if (fileUploadSuccess) {
    hideSuccessClass = '';
  }

  // In progress state
  if (showDropZoneUploading) {
    renderBlock = (
      <div className="upload-dropzone">
        <div className="upload-dropzone__in-progress">
          <i className="icon-doc" aria-hidden="true" />
          <span className="upload-dropzone__title">Uploading...</span>
          <div>
            <i className="icon-animate-spin icon-spin" />
            {fileName}
          </div>
        </div>
      </div>

    );
  }

  // Success state
  if (file || delayFile) {
    renderBlock = (
      <div className="upload-dropzone">
          <div className="upload-dropzone__area-success">
            <div className="upload-dropzone__uploaded-header">
              <i className="icon-doc" aria-hidden="true" />
              <span className="upload-dropzone__title">{itemToUpload}</span>
              <a className="upload-dropzone__delete-button" onClick={removeFile}>
                Delete
              </a>
            </div>
              <p className="upload-dropzone__uploaded-file-name">{prettyName}</p>
          </div>
          <div className={'upload-dropzone__success-message ' + hideSuccessClass}>
            <span>Success!</span> File has been uploaded.
          </div>
        </div>
    );
  }

  // Invalid file format
  if (rejectReason === 'file-invalid-type') {
    renderBlock = (
      <div className="upload-dropzone">
        <div className={'upload-dropzone__area ' + getDragStyle()} {...getRootProps()}>
          <div className="upload-dropzone__reject-notice">
            <p>
              Invalid file format.
            </p>
            <span>{`Allowed file formats: ${acceptedFileTypesText}`}</span>
          </div>
          <div className="upload-dropzone__text" >
            <input {...getInputProps({className: 'upload-dropzone__input'})} />
              <span>Drag &amp; drop file here or </span>
              <span className="upload-dropzone__text-select">select file</span>
          </div>
        </div>
      </div>
    );
  }

  // Over max file size
  if (rejectReason === 'file-too-large') {
    renderBlock = (
      <div className="upload-dropzone">
        <div className={'upload-dropzone__area ' + getDragStyle()} {...getRootProps()}>
          <div className="upload-dropzone__reject-notice">
            <p>
              File exceeds the 50MB size limit.
            </p>
          </div>
          <div className="upload-dropzone__text" >
            <input {...getInputProps({className: 'upload-dropzone__input'})} />
              <span>Drag a different file here or </span>
              <span className="upload-dropzone__text-select">select file</span>
          </div>
        </div>
      </div>
    );

  }

  return renderBlock;
};

export { UploadDropZone };
export default UploadDropZone;
